import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Checkbox, IconButton, List } from '@mui/material';
import { Skeleton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSmartDiaryCheckboxIcons } from './hooks/useSmartDiaryCheckboxIcons';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '90%',
    margin: theme.spacing(2, 0, 1)
  },

  summary: {
    display: 'flex',
    marginTop: theme.spacing(-1),
    justifyContent: 'space-between'
  },

  tabContainer: {
    display: 'flex',
    margin: theme.spacing(0, 1, 2),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5)
  },

  tab: {
    width: '25%',
    height: theme.spacing(5),
    background: theme.skelton?.color,
    margin: theme.spacing(0, 1),
    padding: theme.spacing(1)
  },

  contentLeft: {
    display: 'flex',
    width: '40%',
    alignItems: 'center'
  },

  contentRight: {
    display: 'flex',
    width: '30%',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(1),
    alignItems: 'center'
  },

  noteContainer: {
    display: 'flex',
    width: '95%',
    margin: theme.spacing(-0.5, 0, -0.5)
  },

  detailsMain: {
    background: theme.skelton?.color,
    width: theme.spacing(20),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  details: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  detailsRight: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5, 4.75, 0.5, 0.5),
    height: theme.spacing(3.5)
  },

  detailsSummary: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  bottomContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(0, 1, 0, 2),
    height: '100%',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(1.25)
  },

  checkbox: {
    alignSelf: 'center',
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(0.25),
    marginRight: theme.spacing(-0.5)
  },

  smartDiaryLeft: {
    display: 'flex',
    width: '50%',
    alignItems: 'center'
  },

  collapseExpandDay: {
    background: theme.skelton?.color,
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    marginLeft: theme.spacing(0.2)
  },

  noteDate: {
    display: 'flex',
    background: '#e0e0e0',
    justifyContent: 'space-between'
  },

  notes: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },

  noteText1: {
    background: theme.skelton?.color,
    width: '80%',
    height: theme.spacing(3.5),
    margin: theme.spacing(1, 0.5, 0.5)
  },

  noteText2: {
    background: theme.skelton?.color,
    width: '80%',
    height: theme.spacing(3.5),
    margin: theme.spacing(-1.5, 0.5, 0.5)
  },

  lastUpdatedText: {
    marginBottom: theme.spacing(-0.75),
    background: theme.skelton?.color,
    width: '55%',
    height: theme.spacing(3.25)
  },

  applyTimesheet1: {
    background: theme.palette.grey[300],
    width: '40%',
    height: theme.spacing(5)
  },

  applyTimesheet2: {
    background: theme.skelton?.color,
    width: '90%',
    height: theme.spacing(4.5)
  },

  lastRow: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  }
}));

export const SmartDiaryNotesSkeleton: React.FC = () => {
  const classes = useStyles();
  const iconProps = useSmartDiaryCheckboxIcons();
  return (
    <div className={classes.container} data-testid="smart-diary-notes-skeleton">
      <div className={classes.tabContainer}>
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} className={classes.tab}></Skeleton>
        ))}
      </div>
      <div className={classes.summary}>
        <div className={classes.contentLeft}>
          <Checkbox
            disabled
            className={classes.checkbox}
            color="default"
            checked
            {...iconProps}
          ></Checkbox>
          <Skeleton className={classes.detailsMain}></Skeleton>
        </div>
        <div className={classes.contentRight}>
          <Skeleton className={classes.detailsRight}></Skeleton>
        </div>
      </div>
      <div className={classes.noteDate}>
        <div className={classes.contentLeft}>
          <Checkbox
            disabled
            className={classes.checkbox}
            color="default"
            checked
            {...iconProps}
          ></Checkbox>
          <Skeleton className={classes.details}></Skeleton>
        </div>
        <div className={classes.contentRight}>
          <Skeleton className={classes.detailsSummary}></Skeleton>
          <IconButton size="small">
            <ExpandLessIcon />
          </IconButton>
        </div>
      </div>

      <List>
        {[...Array(2)].map((_: number, rowIndex: number) => (
          <div key={`noteList${rowIndex}`} className={classes.summary}>
            <Checkbox
              disabled
              className={classes.checkbox}
              color="default"
              checked
              {...iconProps}
            ></Checkbox>
            <div className={classes.notes}>
              <Skeleton className={classes.noteText1}></Skeleton>
              <Skeleton className={classes.noteText2}></Skeleton>
            </div>
            <div className={classes.contentRight}>
              <Skeleton className={classes.detailsRight}></Skeleton>
            </div>
          </div>
        ))}
      </List>
      <div className={classes.bottomContainer}>
        <div className={classes.lastRow}>
          <Skeleton className={classes.lastUpdatedText} />
          <Skeleton className={classes.lastUpdatedText} />
        </div>
        <Button className={classes.applyTimesheet1}>
          <Skeleton className={classes.applyTimesheet2} />
        </Button>
      </div>
    </div>
  );
};
